import { useAppStorage, useMutation, useObservable } from '@aesop-fables/scrinium';
import { Button, Link, Typography, useTheme } from '@mui/material';
import { useContext, useEffect, useState } from 'react';
import { LicenseAgreementData } from '../api/apis/LicenseAgreementApi';
import { useLocation, useNavigate } from 'react-router';
import { LicenseCompartments, licenseStorageKey } from '../data/license';
import { PricebookBundle } from '../models/PricebookData';
import { useLoading } from '../hooks/useLoading';
import { Toast } from '../models/Toast';
import { ToastbarContext } from '../App';
import Modal from './Modal';
import Spacer from './Spacer';
import Column from './Column';
import { UpdateSignUpState } from '../data/signUp/mutations';
import { OrgSignUpStateData } from '../models/SignUpData';
import { SignUpCompartments, signUpStorageKey } from '../data/signUp';
import { useCommands } from '../helpers/useCommands';
import { InvalidateAgreement } from '../data/license/commands/InvalidateAgreement';
import { useService } from '@aesop-fables/containr-react';
import { SignUpWizard, signUpWizardKey } from '../services/signUp';
import { useDeviceType } from '../hooks/useDeviceType';
import { useBasePricebookOptions } from '../hooks/usePricebookOptions';

interface CancelContractProps {
  componentType?: 'link' | 'button';
  textStyle?: React.CSSProperties;
  onCancelSuccess?: () => void;
}

const CancelContract: React.FC<CancelContractProps> = ({
  componentType,
  textStyle,
  onCancelSuccess,
}) => {
  const appStorage = useAppStorage();
  const location = useLocation();
  const navigate = useNavigate();
  const { setToast } = useContext(ToastbarContext);
  const { setLoading } = useLoading();
  const commands = useCommands();
  const { isMobile, isTablet } = useDeviceType();

  const licenseDataCache = appStorage.retrieve<LicenseCompartments>(licenseStorageKey);
  const signUpDataCache = appStorage.retrieve<SignUpCompartments>(signUpStorageKey);
  const signUpWizard = useService<SignUpWizard>(signUpWizardKey);

  const pricebookOptions = useBasePricebookOptions() ?? [];
  const licenseAgreements = useObservable(
    licenseDataCache.observe$<LicenseAgreementData[]>('licenseAgreement'),
  );
  const pendingAgreement =
    licenseAgreements &&
    licenseAgreements?.find(
      licenseAgreement => licenseAgreement.docStatus && !licenseAgreement.hasAccepted,
    );
  const orgSignUpState = useObservable(
    signUpDataCache.observe$<OrgSignUpStateData>('orgSignUpState'),
  );
  const multipleProductTypesAvailable = useObservable(signUpWizard.multipleProductTypes$) ?? false;

  const [open, setOpen] = useState<boolean>(false);
  const [doc, setDoc] = useState<LicenseAgreementData>();
  const updateSignUpState = useMutation(new UpdateSignUpState());
  const resumeAddPackage = location?.state?.redirectReason === 'abandonedAddPackage';
  const pricebook: PricebookBundle =
    orgSignUpState?.state?.pricebook && JSON.parse(orgSignUpState?.state.pricebook);
  const selectedPricebook = pricebookOptions?.find(
    selectedPricebook => selectedPricebook.pricebook.id === pricebook?.pricebook?.id,
  );

  useEffect(() => {
    const doc = pendingAgreement
      ? pendingAgreement
      : licenseAgreements &&
        licenseAgreements?.find(
          license => license.docStatus && license.pricebookId === selectedPricebook?.pricebook.id,
        );
    if (doc) {
      setDoc(doc);
    }
  }, [licenseAgreements, pendingAgreement, resumeAddPackage, selectedPricebook?.pricebook.id]);

  const onSubmit = async () => {
    try {
      setLoading(true);
      if (doc) {
        await commands.execute(InvalidateAgreement, doc.id);
        setToast(
          new Toast({
            message: 'Contract canceled!',
            severity: 'success',
            open: true,
            autoHideDuration: 2000,
          }),
        );
      }
      const onboardingComplete = orgSignUpState?.onboardingComplete;
      const singleProductOnboarding = !onboardingComplete && !multipleProductTypesAvailable;

      await updateSignUpState.action({
        state: {
          ...orgSignUpState?.state,
          pricebook: JSON.stringify(null),
          licenseData: JSON.stringify(null),
          ...(singleProductOnboarding ? {} : { product: JSON.stringify(null) }),
        },
      });

      if (onboardingComplete) {
        if (!onCancelSuccess) {
          navigate('/addPackage');
        }
      } else {
        await updateSignUpState.action({ lastCompletedStep: 'inviteOtherAdmins' });
        if (multipleProductTypesAvailable) {
          navigate('/onboarding/chooseProduct', { replace: true });
        } else {
          navigate('/onboarding/license', { replace: true });
        }
      }

      onCancelSuccess?.();
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {componentType === 'button' ? (
        <Button
          className={isMobile || isTablet ? 'mobile' : 'cancelContract'}
          onClick={() => setOpen(true)}
          variant='outlined'
          color='secondary'>
          Cancel contract
        </Button>
      ) : (
        <Link
          onClick={() => setOpen(true)}
          variant='p16'
          color='error.active'
          style={textStyle}
          sx={{
            display: 'flex',
            flex: isMobile || isTablet ? 1 : '0 0 125px',
          }}>
          Cancel contract
        </Link>
      )}
      {open && (
        <Modal
          open={open}
          setOpen={setOpen}
          handleSave={onSubmit}
          primaryButtonText='Yes, cancel'
          secondaryButtonText='No, keep'>
          <Column style={{ padding: '0px 0px 20px 15px', marginTop: '-20px' }}>
            <Typography variant='p32Light' color='secondary'>
              Cancel contract?
            </Typography>
            <Spacer height='xs' />
            <Typography>
              Are you sure you want to cancel the current contract and abandon this process?
            </Typography>
            <Spacer height='xs' />
            <Typography>This action cannot be undone.</Typography>
          </Column>
        </Modal>
      )}
    </>
  );
};

export default CancelContract;

export const useCancelContractToast = (options?: {
  message?: string;
  onCancelSuccess?: () => void;
}) => {
  const { setToast } = useContext(ToastbarContext);
  const { palette } = useTheme();

  const cancelLinkStyle: React.CSSProperties = {
    display: 'inline',
    marginLeft: 10,
    fontSize: 14,
    color: palette.error.dark,
    textDecorationColor: palette.error.dark,
    textDecorationThickness: 1.5,
  };

  const message =
    options?.message ?? 'To make any changes, you need to first cancel your current contract.';

  const handleCancelSuccess = () => {
    setToast(new Toast({ open: false }));
    options?.onCancelSuccess?.();
  };

  return () => {
    setToast(
      new Toast({
        message,
        actionElement: (
          <CancelContract textStyle={cancelLinkStyle} onCancelSuccess={handleCancelSuccess} />
        ),
        severity: 'error',
        open: true,
        autoHideDuration: 6000,
      }),
    );
  };
};
